import React, { useState, useEffect, useReducer, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table } from 'semantic-ui-react';
import { UnfilteredFeedback } from '../../apis/index';
import { getAllChatHistoryCached } from '../../actions/dataConsoleAction';
import DashLoader from '../../components/DashLoader';
import '../../styles/FeedbackDashboard.css';
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import * as XLSX from 'xlsx';

// Define the table columns
const columns = [
  { key: '_id', label: 'ID', minwidth: '10rem', hidden: true },
  { key: 'chatConversationId', label: 'Chat Conversation ID', minwidth: '20rem', hidden: true },
  { key: 'userId', label: 'User ID', minwidth: '20rem', hidden: true },
  { key: 'uuid', label: 'UUID', minwidth: '20rem', hidden: true },
  { key: 'chatMetaId', label: 'Chat ID', minwidth: '20rem', hidden: true },
  { key: 'datasetId', label: 'Dataset ID', minwidth: '20rem', hidden: true },
  { key: 'likeType', label: 'Like Type', minwidth: '10rem', hidden: true },
  { key: 'Title', label: 'Chat Name', minwidth: '20rem' , hidden: true },
  { key: 'datasetTitle', label: 'Dataset Title', minwidth: '20rem'},
  { key: 'userQuestion', label: 'User Question', minwidth: '20rem' },
  { key: 'aiResponse', label: 'Response', minwidth: '32rem' },
  { key: 'feedbackType', label: 'Feedback Type', minwidth: '10rem' },
  { key: 'suggestionType', label: 'Feedback', minwidth: '10rem' },
  { key: 'reason', label: 'Desired Response', minwidth: '20rem' },
  { key: 'username', label: 'Created By', minwidth: '20rem' },
  { key: 'createdUTC', label: 'Created At', minwidth: '20rem' },
  { key: 'chatUrl', label: 'Chat URL', minwidth: '20rem' },
];

// Reducer for managing feedback data and sorting
const initialState = {
  data: [],
  direction: null,
};

function feedbackReducer(state, action) {
  switch (action.type) {
    case 'SET_DATA':
      return { ...state, data: Array.isArray(action.data) ? action.data : [] };
    case 'TOGGLE_SORT':
      return {
        ...state,
        data: state.data.slice().reverse(),
        direction: state.direction === 'ascending' ? 'descending' : 'ascending',
      };
    default:
      throw new Error('Unknown action type');
  }
}

const FeedbackDashboard = () => {
  const [state, dispatch] = useReducer(feedbackReducer, initialState);
  const { data, direction } = state;
  const [datasets, setDatasets] = useState([]);
  const [selectedDataset, setSelectedDataset] = useState('');
  const [allChatHistory, setAllChatHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const hasFetchedRef = useRef(false);

  const reduxDispatch = useDispatch();
  const allChatHistoryCache = useSelector((state) => state.allChatHistoryRed);

  const fetchFeedbackData = async (chatHistoryData) => {
    try {
      const response = await UnfilteredFeedback();

      // Add the chatUrl to the feedback data
      const feedbackData = response.data.map(feedback => {
        const chatId = feedback.chatMetaId || '';
        const datasetId = feedback.datasetId || '';
        const datasetName = feedback.datasetTitle || '';
        const matchingChat = chatHistoryData.find(chat => chat.id === chatId);
        const Title = matchingChat ? matchingChat.title : '';
        const baseUrl = window.location.origin;
        const chatUrl = `${baseUrl}/aiassistant?c=${datasetId}&cid=${chatId}&title=${datasetName}`;

        return { ...feedback, chatUrl, Title };
      });

      dispatch({ type: 'SET_DATA', data: feedbackData });

      // Extract unique dataset titles
      const uniqueDatasets = [...new Set(feedbackData.map(item => item.datasetTitle))];
      setDatasets(uniqueDatasets);
    } catch (error) {
      console.error('Error fetching feedback data:', error);
      dispatch({ type: 'SET_DATA', data: [] });
    }
  };

  const handleExportToExcel = () => {
    const filteredData = selectedDataset
      ? state.data.filter((feedback) => feedback.datasetTitle === selectedDataset)
      : state.data;
  
    if (filteredData.length === 0) {
      alert('No data available for the selected dataset.');
      return;
    }
  
    // Prepare data for Excel export
    // const formattedData = filteredData.map((item) => {
    //   const rowData = {};
    //   columns.forEach(({ key, label }) => {
    //     rowData[label] = item[key] || ''; // Use column label as the header 
    //     // hidden = hidden || false; // Respect the `hidden` property for each column       
    //   });
    //   return rowData;
    // });

    const formattedData = filteredData.map((item) => {
      const rowData = {};
      columns.forEach(({ key, label }) => {
        if (key === 'createdUTC') {
          // Convert UTC time to local time
          const localTime = new Date(item[key]).toLocaleString();
          rowData[label] = localTime;
        } else {
          rowData[label] = item[key] || ''; // Use column label as the header
        }
      });
      return rowData;
    });
  
    // Create the worksheet
    const worksheet = XLSX.utils.json_to_sheet(formattedData);

     // Define column visibility for specific columns (A to G hidden)
  const totalColumns = Object.keys(formattedData[0] || {}).length;
  worksheet['!cols'] = Array.from({ length: totalColumns }, (_, index) => ({
    hidden: index >= 0 && index <= 7, // Hide columns A (0) to G (6)
    // wpx: 100,
    wpx: columns[index]?.key === 'chatUrl' ? 200 : 100, 
  }));



     // Ensure the rest of the columns are explicitly marked as visible
    worksheet['!cols'] = worksheet['!cols'].map((col, index) => col || { hidden: false });

    // worksheet.getRange('A:G').columnHidden = true;
  
    // Define the visibility of columns in Excel
    // worksheet["!cols"] = columns.map((col) => ({
    //   hidden: col.hidden || false, // Respect the `hidden` property for each column
    // }));
  
    

    // Create a workbook and append the worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Feedback Data');
  
    // Export the Excel file
    XLSX.writeFile(workbook, `${selectedDataset || 'All'}_FeedbackData.xlsx`);
  };
  
  

  

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    (async () => {
      try {
        setIsLoading(true);
        // Use Redux-cached chat history (fetches only once across dashboards)
        const chatHistoryData = await reduxDispatch(getAllChatHistoryCached());
        setAllChatHistory(chatHistoryData);
        await fetchFeedbackData(chatHistoryData);
      } catch (error) {
        console.error('Error fetching feedback dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const handleDatasetChange = (e) => {
    setSelectedDataset(e.target.value);
  };

  const formatDate = (utcDate) => {
    const date = new Date(utcDate);
    return date.toLocaleString();
  };

  return (
    <div className="feedback-dashboard-container" style={{ position: 'relative', minHeight: '300px' }}>
      {isLoading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.85)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <DashLoader />
        </div>
      )}
      <div className="dropdown-container">
        <div className="fd-dropdown">
          <select onChange={handleDatasetChange} value={selectedDataset}>
            <option value="" disabled>Choose Dataset</option>
            {datasets.map(dataset => (
              <option key={dataset} value={dataset}>{dataset}</option>
            ))}
          </select>
        </div>
        <div className="export-btn-container">
          <button className="add-btn" onClick={handleExportToExcel}>
            Export to Excel
          </button>
        </div>
      </div>

      <div className="horizontal-scroll feedback-table-container">
        <Table padded className="feedback-table">
          <Table.Header>
            <Table.Row className="feedback-head" onClick={() => dispatch({ type: 'TOGGLE_SORT' })}>
              {columns.map(columnItem => (
                !columnItem.hidden && (
                  <Table.HeaderCell
                    key={columnItem.key}
                    sorted={direction}
                    style={{ width: columnItem.minwidth }}
                  >
                    {columnItem.label}
                  </Table.HeaderCell>
                )
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {data.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={columns.length} className="no-data-cell">
                  Data is not available
                </Table.Cell>
              </Table.Row>
            ) : (
              data
                .filter(feedback => !selectedDataset || feedback.datasetTitle === selectedDataset)
                .map((feedback, index) => (
                  <Table.Row className="feedback-head feedback-nogap" key={index}>
                    {columns.map(columnItem => (
                      !columnItem.hidden && (
                        <Table.Cell
                          key={columnItem.key}
                          className={columnItem.key === 'datasetTitle' ? 'bold-text' : ''}
                          style={{ width: columnItem.minwidth }}
                        >
                          {columnItem.key === 'aiResponse' ? (
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                              {feedback[columnItem.key]}
                            </ReactMarkdown>
                          ) : columnItem.key === 'createdUTC' ? (
                            formatDate(feedback[columnItem.key])
                          ) : columnItem.key === 'chatUrl' ? (
                            feedback.Title ? (
                              <a href={feedback[columnItem.key]} target="_self" rel="noreferrer">
                                {feedback.Title}
                              </a>
                            ) : (
                              <span className="red-text">Chat not found</span>
                            )
                          ) : (
                            feedback[columnItem.key]
                          )}
                        </Table.Cell>
                      )
                    ))}
                  </Table.Row>
                ))
            )}
          </Table.Body>
        </Table>
      </div>
    </div>
  );
};

export default FeedbackDashboard;
